/* global globalThis */
// Pure helpers for the "A/B Testing" campaign type: sxi_content keys, arms, weights, lifecycle
// and result rows. No React and no network, so all of it is unit-tested.
// Spec: docs/superpowers/specs/2026-09-11-ab-testing-campaign-type-design.md
import { variantStatsVsControl } from './abStats.js';

export const AB_CATEGORY = 'A/B Testing';
// Page access id that unlocks the A/B Testing category (granted per store from the Admin
// Dashboard, like Keyword Personalization 19 and Ads Personalization 27).
export const AB_TESTING_PAGE_ID = 72;

// Whether the store's page grants include A/B Testing. `allowedPagesJson` is the
// allowed_pages_json cookie object ({ <shopKey>: { <Page Name>: <id> } }).
export function canUseAbTesting(allowedPagesJson, shopDomain) {
    const shopKey = String(shopDomain || '').replace('.myshopify.com', '');
    const pages = allowedPagesJson && shopKey ? allowedPagesJson[shopKey] : null;
    if (!pages || typeof pages !== 'object') return false;
    return Object.values(pages).map(Number).includes(AB_TESTING_PAGE_ID);
}
export const AB_SXI_RE = /^sxi_abt_[0-9a-f]{8}_[0-9a-f]{6}_[a-z]$/;
export const ARM_LETTERS = 'abcdefghijklmnopqrstuvwxyz';
// The api-worker accepts weights whose sum is within this of 1.
export const WEIGHT_TOLERANCE = 0.001;

// Origin + path: no query, no fragment, no trailing slash. The ONE string every downstream match
// (feature flags, worker mapping, experiment) is keyed on. '' when the URL can't be parsed.
export function normalizeLandingPage(url) {
    if (!url || typeof url !== 'string') return '';
    try {
        const u = new URL(url.trim());
        return (u.origin + u.pathname).replace(/\/+$/, '');
    } catch {
        return '';
    }
}

// Lowercase host + path, no trailing slash: the input page8 hashes.
export function pageKey(url) {
    if (!url || typeof url !== 'string') return '';
    try {
        const u = new URL(url.trim());
        return (u.host.toLowerCase() + u.pathname).replace(/\/+$/, '');
    } catch {
        return '';
    }
}

export async function sha256Hex(text) {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export const armKey = (letter) => `variant_${letter}`;
export const armLetter = (key) => (/^variant_([a-z])$/.exec(key || '') || [])[1] || null;

// sxi_abt_<page8>_<campaign6>_<arm>
export async function abSxiContent(landingPage, campaignId, letter) {
    const key = pageKey(landingPage);
    if (!key) throw new Error('A valid landing page URL is required');
    if (typeof letter !== 'string' || letter.length !== 1 || !ARM_LETTERS.includes(letter)) {
        throw new Error(`Invalid arm letter: ${letter}`);
    }
    const campaign6 = String(campaignId || '').replace(/-/g, '').toLowerCase().slice(0, 6);
    if (!/^[0-9a-f]{6}$/.test(campaign6)) throw new Error('campaign_id must start with 6 hex characters');
    const page8 = (await sha256Hex(key)).slice(0, 8);
    return `sxi_abt_${page8}_${campaign6}_${letter}`;
}

// The first letter no current variant row uses; null once all 26 are taken.
export function nextArmLetter(variants) {
    const used = new Set((variants || []).map((v) => armLetter(v?.ab_arm)).filter(Boolean));
    for (const letter of ARM_LETTERS) {
        if (!used.has(letter)) return letter;
    }
    return null;
}

// n equal weights: each arm gets the same 4-decimal share and the last arm takes the rounding
// remainder, so the sum is exactly 1 however many arms there are.
export function equalizeWeights(n) {
    if (!Number.isInteger(n) || n < 1) return [];
    const share = Math.floor(10000 / n) / 10000;
    const weights = new Array(n).fill(share);
    weights[n - 1] = Math.round((1 - share * (n - 1)) * 10000) / 10000;
    return weights;
}

// Control first, then one arm per variant row, all equal.
export function equalArms(variants) {
    const rows = variants || [];
    const weights = equalizeWeights(rows.length + 1);
    return [
        { key: 'control', weight: weights[0] },
        ...rows.map((v, i) => ({ key: v.ab_arm, weight: weights[i + 1] })),
    ];
}

export function weightsValid(weights) {
    return Array.isArray(weights)
        && weights.length >= 2
        && weights.every((w) => Number.isFinite(w) && w >= 0)
        && Math.abs(weights.reduce((sum, w) => sum + w, 0) - 1) <= WEIGHT_TOLERANCE;
}

export const toPercent = (weight) => Math.round((Number(weight) || 0) * 10000) / 100;
export const fromPercent = (percent) => Math.round((Number(percent) || 0) * 100) / 10000;

// A variant row is Defined once its editor has saved a key and a content object.
export function variantDefined(row) {
    return !!(row
        && Array.isArray(row.targeting) && typeof row.targeting[0] === 'string' && row.targeting[0]
        && row.value && typeof row.value === 'object' && !Array.isArray(row.value));
}

export function newAbTest() {
    return {
        arms: [{ key: 'control', weight: 1 }],
        traffic_split: 1,
        goal_metric: '',
        goal_selectors: [],
        hypothesis: '',
        experiment_id: null,
        status: 'draft',
        started_at: null,
        ended_at: null,
    };
}

const STATUS_LABELS = { draft: 'Draft', running: 'Running', paused: 'Paused', completed: 'Completed' };

export function experimentStatusLabel(abTest) {
    if (!abTest?.experiment_id) return 'Not created';
    return STATUS_LABELS[abTest.status] || 'Draft';
}

// The sxi-status-badge modifier (ChatApp.css) the experiment status is shown in: green only while
// visitors are in the test, grey before and after it.
const STATUS_TONES = { running: 'live', paused: 'paused', completed: 'default' };

export function experimentStatusTone(abTest) {
    if (!abTest?.experiment_id) return 'draft';
    return STATUS_TONES[abTest.status] || 'draft';
}

// The ab_test fields only the /api/cux/ab routes write (SERVER_OWNED_AB_FIELDS, server/worker/ab_campaign.js).
export const AB_SERVER_OWNED_FIELDS = ['experiment_id', 'status', 'started_at', 'ended_at'];

// A lifecycle response merged into the page's ab_test: only the server-owned fields, so unsaved
// edits to arms, weights or the goal survive it.
export function mergeLifecycleAbTest(local, serverAbTest) {
    const next = { ...(local || {}) };
    if (!serverAbTest) return next;
    for (const field of AB_SERVER_OWNED_FIELDS) {
        if (field in serverAbTest) next[field] = serverAbTest[field];
    }
    return next;
}

// Variants, weights and traffic can change until a test runs, and again while it is paused (the
// api-worker's ASSIGNMENT_EDITABLE_IN). One rule for the A/B section and the variant editor.
export function abArmsEditable(abTest) {
    if (!abTest?.experiment_id) return true;
    const status = abTest.status || 'draft';
    return status === 'draft' || status === 'paused';
}

// Start and Resume lock variants, weights and traffic again, so the page is published first and
// the test runs what it shows (the server pushes the stored settings before the status call).
export function publishesBeforeStatus(abTest, status) {
    return status === 'running' && abArmsEditable(abTest);
}

// The line under the lifecycle buttons: how to edit now, and what an edit while paused costs.
export function abEditingNote(abTest) {
    if (!abTest?.experiment_id) return null;
    switch (abTest.status) {
        case 'running':
            return 'Pause the test to change its variants, weights, traffic or variant content.';
        case 'paused':
            return 'Changes take effect when you resume. Some returning visitors will switch variant, and results from before and after the change are counted together. To stop showing a variant, set its weight to 0.';
        case 'completed':
            return 'A completed test can\'t be changed.';
        default:
            return null;
    }
}

// Whether a variant row can be removed. Once a test has run, a variant that may have been part of
// it stays: the next Add would reuse its letter and key, pooling new content with its results
// (weight 0 stops it instead). A variant without content never ran (Start and Resume need content
// for every variant), so it can always go.
export function variantRemovable(abTest, row) {
    if (!abArmsEditable(abTest)) return false;
    return !abTest?.started_at || !variantDefined(row);
}

// Which lifecycle controls show, from the campaign's ab_test and its variant rows.
export function lifecycleActions(abTest, variants) {
    const rows = variants || [];
    if (!abTest?.experiment_id) {
        const ready = rows.length > 0
            && rows.every(variantDefined)
            && weightsValid((abTest?.arms || []).map((a) => a.weight));
        return { create: true, createEnabled: ready, save: false, start: false, pause: false, resume: false, complete: false, editable: true };
    }
    const status = abTest.status || 'draft';
    return {
        create: false,
        createEnabled: false,
        save: status !== 'completed',
        start: status === 'draft',
        pause: status === 'running',
        resume: status === 'paused',
        complete: status === 'running' || status === 'paused',
        editable: abArmsEditable(abTest),
    };
}

// One results row per arm (in `arms` order, control first), each variant compared with control.
export function buildResultRows(arms, variants, totals, opts = {}) {
    const byKey = totals || {};
    const control = byKey.control || { impressions: 0, conversions: 0 };
    return (arms || []).map((arm) => {
        const isControl = arm.key === 'control';
        const stats = variantStatsVsControl(
            isControl ? null : control,
            byKey[arm.key] || { impressions: 0, conversions: 0 },
            opts,
        );
        const row = (variants || []).find((v) => v.ab_arm === arm.key);
        return {
            key: arm.key,
            name: isControl ? 'Page as authored' : (row?.name || arm.key),
            impressions: stats.impressions,
            conversions: stats.conversions,
            rate: stats.rate,
            rateCi: stats.rateCi,
            uplift: isControl ? null : (stats.zTest?.relativeLift ?? null),
            pValue: isControl ? null : (stats.zTest?.pValue ?? null),
            probBeatsControl: isControl ? null : (stats.bayes?.probVariantBeatsControl ?? null),
        };
    });
}
